<template>
  <div id="bulk-update-instances" class="bulk-update-instances">
    <div class="bulk-update-shell">
      <section class="bulk-update-intro">
        <div class="bulk-update-intro__copy">
          <span class="bulk-update-kicker">Operação interna do Backoffice</span>
          <h3 class="bulk-update-instances__title">Atualizar Conexões em Massa</h3>
          <p class="bulk-update-instances__subtitle">
            Selecione as dashes, defina exatamente quais conexões serão afetadas e aplique uma única ação com revisão final antes do envio.
          </p>
        </div>

        <div class="bulk-update-intro__side">
          <div class="bulk-update-intro__stats">
            <div class="bulk-update-intro__stat">
              <small>Tenants totais do Backoffice</small>
              <strong>{{ loadingTenants ? '...' : sortedTenants.length }}</strong>
            </div>

            <div class="bulk-update-intro__stat">
              <small>Conexões selecionadas</small>
              <strong>{{ selectedConnectionsCount }}</strong>
            </div>
          </div>

          <vs-button type="border" color="dark" @click="resetWizard">
            Limpar fluxo
          </vs-button>
        </div>
      </section>

      <section class="bulk-update-wizard">
        <aside class="bulk-update-sidebar">
          <div class="bulk-update-sidebar__panel">
            <div class="bulk-update-sidebar__header">
              <span class="bulk-update-sidebar__eyebrow">Passo a passo</span>
              <strong>Fluxo guiado</strong>
            </div>

            <div class="bulk-update-sidebar__steps">
              <button type="button" class="bulk-update-sidebar-step" :class="stepClass(1)" @click="goToStep(1)">
                <span class="bulk-update-sidebar-step__marker">1</span>
                <span class="bulk-update-sidebar-step__content">
                  <small>Etapa 1</small>
                  <strong>Seleção de tenants</strong>
                  <span>Escolha as dashes que terão conexões alteradas.</span>
                </span>
              </button>

              <button type="button" class="bulk-update-sidebar-step" :class="stepClass(2)" @click="goToStep(2)">
                <span class="bulk-update-sidebar-step__marker">2</span>
                <span class="bulk-update-sidebar-step__content">
                  <small>Etapa 2</small>
                  <strong>Seleção de conexões</strong>
                  <span>Defina quais conexões de cada tenant entram no lote.</span>
                </span>
              </button>

              <button type="button" class="bulk-update-sidebar-step" :class="stepClass(3)" @click="goToStep(3)">
                <span class="bulk-update-sidebar-step__marker">3</span>
                <span class="bulk-update-sidebar-step__content">
                  <small>Etapa 3</small>
                  <strong>Ação da atualização</strong>
                  <span>Escolha a configuração final e os status aplicáveis.</span>
                </span>
              </button>

              <button type="button" class="bulk-update-sidebar-step" :class="stepClass(4)" @click="goToStep(4)">
                <span class="bulk-update-sidebar-step__marker">4</span>
                <span class="bulk-update-sidebar-step__content">
                  <small>Etapa 4</small>
                  <strong>Revisão final</strong>
                  <span>Revise os alvos, confirme e execute a operação.</span>
                </span>
              </button>
            </div>
          </div>

          <div class="bulk-update-sidebar__panel bulk-update-sidebar__panel--summary">
            <div class="bulk-update-sidebar__header">
              <span class="bulk-update-sidebar__eyebrow">Resumo atual</span>
              <strong>Painel rápido</strong>
            </div>

            <div class="bulk-update-sidebar__mini-grid">
              <div class="bulk-update-mini-stat">
                <small>Tenants</small>
                <strong>{{ reviewTenantGroups.length || selectedTenantNames.length }}</strong>
              </div>
              <div class="bulk-update-mini-stat">
                <small>Conexões</small>
                <strong>{{ selectedConnectionsCount }}</strong>
              </div>
              <div class="bulk-update-mini-stat">
                <small>Configuração</small>
                <strong>{{ configurationSummaryLabel }}</strong>
              </div>
              <div class="bulk-update-mini-stat">
                <small>Status</small>
                <strong>{{ statusSummaryLabel }}</strong>
              </div>
            </div>

            <div class="bulk-update-sidebar__hint">
              <strong>Importante</strong>
              <p>As validações de tenant sem conexão, limpeza do fluxo e retorno entre etapas continuam ativas durante todo o wizard.</p>
            </div>
          </div>
        </aside>

        <div class="bulk-update-stage">
          <div v-if="currentStep === 1" class="bulk-update-stage__surface">
            <div class="bulk-update-stage__header">
              <div>
                <span class="bulk-update-stage__eyebrow">Etapa 1</span>
                <h4>Seleção de tenants</h4>
                <p>Escolha as dashes que terão as conexões analisadas para a atualização em massa.</p>
              </div>

              <div class="bulk-update-stage__counter">
                {{ selectedTenantNames.length }} dash(s) selecionada(s)
              </div>
            </div>

            <div class="bulk-update-search-card mt-4">
              <div class="bulk-update-search-card__copy">
                <strong>Filtre e monte o lote inicial</strong>
                <p>Você pode buscar pelo nome do tenant, selecionar todos os resultados visíveis ou limpar a seleção atual a qualquer momento.</p>
              </div>

              <div class="bulk-update-toolbar">
                <vs-input v-model="tenantSearch" label="Buscar tenant" class="w-full" />

                <div class="bulk-update-toolbar__actions">
                  <vs-button size="small" type="border" @click="selectAllFilteredTenants">
                    Selecionar filtradas
                  </vs-button>
                  <vs-button size="small" type="border" color="dark" @click="clearTenantSelection">
                    Limpar seleção
                  </vs-button>
                </div>
              </div>
            </div>

            <div class="bulk-update-tenant-grid mt-4">
              <label
                v-for="tenant in paginatedFilteredTenants"
                :key="tenantKey(tenant)"
                class="bulk-update-tenant-card"
                :class="{ 'bulk-update-tenant-card--selected': isTenantSelected(tenantName(tenant)) }"
              >
                <div class="bulk-update-tenant-card__top">
                  <span class="bulk-update-tenant-card__avatar">
                    {{ tenantName(tenant).charAt(0).toUpperCase() }}
                  </span>

                  <input
                    type="checkbox"
                    class="bulk-update-checkbox"
                    :checked="isTenantSelected(tenantName(tenant))"
                    @change="toggleTenantSelection(tenantName(tenant), $event.target.checked)"
                  >
                </div>

                <div class="bulk-update-tenant-card__body">
                  <strong>{{ tenantName(tenant) }}</strong>
                  <small>
                    <template v-if="tenant.cnpj">CNPJ {{ tenant.cnpj }}</template>
                    <template v-else>Tenant sem CNPJ informado</template>
                  </small>
                </div>

                <div class="bulk-update-tenant-card__footer">
                  <span>{{ isTenantSelected(tenantName(tenant)) ? 'Incluído no lote' : 'Disponível para inclusão' }}</span>
                </div>
              </label>

              <div v-if="!filteredTenants.length" class="bulk-update-empty">
                Nenhum tenant encontrado para o filtro atual.
              </div>
            </div>

            <div v-if="filteredTenants.length" class="bulk-update-pagination mt-4">
              <div class="bulk-update-pagination__info">
                <strong>{{ tenantPaginationSummary }}</strong>
                <small>{{ totalTenantPages }} página(s) com até {{ tenantPageSize }} tenant(s) por página</small>
              </div>

              <div class="bulk-update-pagination__controls">
                <vs-button
                  size="small"
                  type="border"
                  color="dark"
                  :disabled="currentTenantPage === 1"
                  @click="previousTenantPage"
                >
                  Página anterior
                </vs-button>

                <span class="bulk-update-pagination__current">
                  Página {{ currentTenantPage }} de {{ totalTenantPages }}
                </span>

                <vs-button
                  size="small"
                  type="border"
                  :disabled="currentTenantPage >= totalTenantPages"
                  @click="nextTenantPage"
                >
                  Próxima página
                </vs-button>
              </div>
            </div>

            <div class="bulk-update-stage__actions mt-4">
              <vs-button color="primary" :disabled="!canAdvanceTenantStep || loadingInstances" @click="loadConnectionsFromSelectedTenants">
                {{ loadingInstances ? 'Carregando conexões...' : 'Avançar para conexões' }}
              </vs-button>
            </div>
          </div>

          <div v-if="currentStep === 2" class="bulk-update-stage__surface">
            <div class="bulk-update-stage__header">
              <div>
                <span class="bulk-update-stage__eyebrow">Etapa 2</span>
                <h4>Seleção de conexões</h4>
                <p>Escolha exatamente quais conexões de cada tenant farão parte do lote.</p>
              </div>

              <div class="bulk-update-stage__counter">
                {{ selectedConnectionsCount }} conexão(ões) selecionada(s)
              </div>
            </div>

            <div v-if="tenantsWithoutConnections.length || tenantLoadErrors.length" class="bulk-update-alert-grid mt-4">
              <div v-if="tenantsWithoutConnections.length" class="bulk-update-alert bulk-update-alert--warning">
                <strong>Tenants removidos da seleção</strong>
                <span>{{ tenantsWithoutConnections.join(', ') }}</span>
              </div>

              <div v-if="tenantLoadErrors.length" class="bulk-update-alert bulk-update-alert--danger">
                <strong>Falha ao carregar conexões</strong>
                <span>{{ tenantLoadErrors.map(item => `${item.tenantName} (${item.error})`).join(', ') }}</span>
              </div>
            </div>

            <div class="bulk-update-search-card mt-4">
              <div class="bulk-update-search-card__copy">
                <strong>Refine o lote por conexão</strong>
                <p>Busque por nome, telefone, ID ou modo da conexão. Você também pode selecionar tudo que estiver visível no momento.</p>
              </div>

              <div class="bulk-update-toolbar">
                <vs-input v-model="connectionSearch" label="Buscar conexão" class="w-full" />

                <div class="bulk-update-toolbar__actions">
                  <vs-button size="small" type="border" @click="selectAllVisibleConnections">
                    Selecionar visíveis
                  </vs-button>
                  <vs-button size="small" type="border" color="dark" @click="clearAllConnectionsSelection">
                    Limpar conexões
                  </vs-button>
                </div>
              </div>
            </div>

            <div class="bulk-update-groups mt-4">
              <div v-for="group in availableTenantGroups" :key="`group-${group.tenantName}`" class="bulk-update-group">
                <div class="bulk-update-group__header">
                  <div class="bulk-update-group__copy">
                    <span class="bulk-update-group__eyebrow">{{ tenantDisplayName(group.tenant) }}</span>
                    <h5>{{ group.filteredInstances.length }} conexão(ões) visível(is)</h5>
                    <small>{{ (selectedConnectionsByTenant[group.tenantName] || []).length }} selecionada(s) neste tenant</small>
                  </div>

                  <div class="bulk-update-group__actions">
                    <vs-button size="small" type="border" @click="selectTenantVisibleConnections(group.tenantName)">
                      Selecionar deste tenant
                    </vs-button>
                    <vs-button size="small" type="border" color="dark" @click="clearTenantConnections(group.tenantName)">
                      Limpar
                    </vs-button>
                  </div>
                </div>

                <div class="bulk-update-connection-grid">
                  <label
                    v-for="instance in group.filteredInstances"
                    :key="`${group.tenantName}-${instance.id}`"
                    class="bulk-update-connection-card"
                    :class="{ 'bulk-update-connection-card--selected': isConnectionSelected(group.tenantName, instance.id) }"
                  >
                    <div class="bulk-update-connection-card__top">
                      <div class="bulk-update-connection-card__identity">
                        <strong>{{ instance.name }}</strong>
                        <small>ID {{ instance.id }}<template v-if="instance.phone_number"> | {{ instance.phone_number }}</template></small>
                      </div>

                      <input
                        type="checkbox"
                        class="bulk-update-checkbox"
                        :checked="isConnectionSelected(group.tenantName, instance.id)"
                        @change="toggleConnectionSelection(group.tenantName, instance.id, $event.target.checked)"
                      >
                    </div>

                    <div class="bulk-update-connection-card__badges">
                      <span class="bulk-badge bulk-badge--primary">{{ formatInstanceMode(instance) }}</span>
                      <span class="bulk-badge" :class="instance.activated ? 'bulk-badge--success' : 'bulk-badge--neutral'">
                        {{ instance.activated ? 'Ativa' : 'Inativa' }}
                      </span>
                      <span class="bulk-badge" :class="instance.reset_finished ? 'bulk-badge--success' : 'bulk-badge--neutral'">
                        {{ instance.reset_finished ? 'Reset finalizado' : 'Reset pendente' }}
                      </span>
                    </div>
                  </label>

                  <div v-if="!group.filteredInstances.length" class="bulk-update-empty">
                    Nenhuma conexão encontrada para este tenant com o filtro atual.
                  </div>
                </div>
              </div>
            </div>

            <div class="bulk-update-stage__actions mt-4">
              <vs-button size="small" type="border" color="dark" @click="goToStep(1)">
                Voltar
              </vs-button>
              <vs-button size="small" color="primary" :disabled="!canAdvanceConnectionsStep" @click="goToStep(3)">
                Avançar para ação
              </vs-button>
            </div>
          </div>

          <div v-if="currentStep === 3" class="bulk-update-stage__surface">
            <div class="bulk-update-stage__header">
              <div>
                <span class="bulk-update-stage__eyebrow">Etapa 3</span>
                <h4>Ação da atualização</h4>
                <p>Defina a configuração final das conexões selecionadas e, quando permitido, os status que devem ser alterados.</p>
              </div>
            </div>

            <div class="bulk-update-config-grid mt-4">
              <div class="bulk-update-config-card">
                <div class="bulk-update-card-head">
                  <span class="bulk-update-card-head__eyebrow">Configuração</span>
                  <h5>Tipo final da conexão</h5>
                  <small>Escolha uma única direção para todo o lote selecionado.</small>
                </div>

                <div class="bulk-update-mode-grid mt-4">
                  <label class="bulk-update-choice-card" :class="{ 'bulk-update-choice-card--selected': actionForm.configurationMode === 'keep' }">
                    <input
                      v-model="actionForm.configurationMode"
                      type="radio"
                      class="bulk-update-checkbox"
                      value="keep"
                    >
                    <span class="bulk-update-choice-card__content">
                      <strong>Não alterar configuração</strong>
                      <small>Mantém o tipo atual de cada conexão.</small>
                    </span>
                  </label>

                  <label class="bulk-update-choice-card" :class="{ 'bulk-update-choice-card--selected': actionForm.configurationMode === 'official' }">
                    <input
                      v-model="actionForm.configurationMode"
                      type="radio"
                      class="bulk-update-checkbox"
                      value="official"
                    >
                    <span class="bulk-update-choice-card__content">
                      <strong>Converter para Oficial / Cloud</strong>
                      <small>Aplica integração oficial com fornecedor Dialog, Vonage, Messenger, Instagram ou YCloud.</small>
                    </span>
                  </label>

                  <label class="bulk-update-choice-card" :class="{ 'bulk-update-choice-card--selected': actionForm.configurationMode === 'web' }">
                    <input
                      v-model="actionForm.configurationMode"
                      type="radio"
                      class="bulk-update-checkbox"
                      value="web"
                    >
                    <span class="bulk-update-choice-card__content">
                      <strong>Converter para Web</strong>
                      <small>Aplica comportamento Web com ilimitada e ativação Cloud quando necessário.</small>
                    </span>
                  </label>
                </div>

                <div v-if="actionForm.configurationMode === 'official'" class="bulk-update-config-block mt-4">
                  <span class="bulk-update-config-label">Fornecedor oficial</span>

                  <div class="bulk-update-provider-grid">
                    <label
                      v-for="provider in officialInstanceProviders"
                      :key="provider.value"
                      class="bulk-update-provider-card"
                      :class="{ 'bulk-update-provider-card--selected': actionForm.officialProvider === provider.value }"
                    >
                      <input
                        v-model="actionForm.officialProvider"
                        type="radio"
                        class="bulk-update-checkbox"
                        :value="provider.value"
                      >
                      <span class="bulk-update-provider-card__content">
                        <strong>{{ provider.label }}</strong>
                        <small>Fornecedor oficial selecionado para o lote.</small>
                      </span>
                    </label>
                  </div>

                  <div class="bulk-update-alert mt-3">
                    <strong>Regra aplicada</strong>
                    <span>Conexões oficiais usam integração Cloud e bloqueiam ajustes manuais de status nesta etapa.</span>
                  </div>
                </div>

                <div v-if="actionForm.configurationMode === 'web'" class="bulk-update-config-block mt-4">
                  <div class="bulk-update-toggle-card">
                    <div>
                      <strong>Conexão Ilimitada</strong>
                      <small>Usa o tipo de conexão ilimitada no faturamento mensal.</small>
                    </div>

                    <vs-switch v-model="actionForm.webUnlimited" color="primary">
                      <span slot="on">Sim</span>
                      <span slot="off">Não</span>
                    </vs-switch>
                  </div>

                  <div class="bulk-update-toggle-card mt-3">
                    <div>
                      <strong>Conexão de Ativação Cloud</strong>
                      <small>Ativa temporariamente a versão para apoio em ativações Cloud.</small>
                    </div>

                    <vs-switch v-model="actionForm.webUseSync" color="warning">
                      <span slot="on">Sim</span>
                      <span slot="off">Não</span>
                    </vs-switch>
                  </div>

                  <div v-if="actionForm.webUseSync" class="bulk-update-alert bulk-update-alert--warning mt-3">
                    <strong>Atenção</strong>
                    <span>Use esta opção somente para ativações Cloud. Conexões mantidas nesta versão serão trocadas automaticamente.</span>
                  </div>
                </div>
              </div>

              <div class="bulk-update-config-card">
                <div class="bulk-update-card-head">
                  <span class="bulk-update-card-head__eyebrow">Status</span>
                  <h5>Ajustes complementares</h5>
                  <small>Altere apenas os status que realmente precisam ser padronizados no lote.</small>
                </div>

                <div v-if="!canConfigureStatus" class="bulk-update-alert mt-4">
                  <strong>Status bloqueado</strong>
                  <span>{{ statusLockMessage }}</span>
                </div>

                <div class="bulk-update-status-list mt-4" :class="{ 'bulk-update-status-list--disabled': !canConfigureStatus }">
                  <div class="bulk-update-status-card">
                    <div class="bulk-update-status-card__header">
                      <vs-checkbox v-model="actionForm.updateResetFinished" :disabled="!canConfigureStatus" size="small">
                        <span>Alterar Reset Finalizado</span>
                      </vs-checkbox>
                    </div>

                    <div class="bulk-update-inline-options">
                      <vs-radio
                        v-model="actionForm.resetFinishedValue"
                        :disabled="!canConfigureStatus || !actionForm.updateResetFinished"
                        :vs-value="true"
                        vs-name="bulkResetFinished"
                      >
                        Marcar como Sim
                      </vs-radio>

                      <vs-radio
                        v-model="actionForm.resetFinishedValue"
                        :disabled="!canConfigureStatus || !actionForm.updateResetFinished"
                        :vs-value="false"
                        vs-name="bulkResetFinished"
                      >
                        Marcar como Não
                      </vs-radio>
                    </div>
                  </div>

                  <div class="bulk-update-status-card mt-3">
                    <div class="bulk-update-status-card__header">
                      <vs-checkbox v-model="actionForm.updateActivated" :disabled="!canConfigureStatus" size="small">
                        <span>Alterar Conexão Ativa</span>
                      </vs-checkbox>
                    </div>

                    <div class="bulk-update-inline-options">
                      <vs-radio
                        v-model="actionForm.activatedValue"
                        :disabled="!canConfigureStatus || !actionForm.updateActivated"
                        :vs-value="true"
                        vs-name="bulkActivated"
                      >
                        Marcar como Sim
                      </vs-radio>

                      <vs-radio
                        v-model="actionForm.activatedValue"
                        :disabled="!canConfigureStatus || !actionForm.updateActivated"
                        :vs-value="false"
                        vs-name="bulkActivated"
                      >
                        Marcar como Não
                      </vs-radio>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="bulk-update-review-strip mt-4">
              <div class="bulk-update-review-strip__item">
                <small>Tenants afetados</small>
                <strong>{{ reviewTenantGroups.length }}</strong>
              </div>
              <div class="bulk-update-review-strip__item">
                <small>Conexões afetadas</small>
                <strong>{{ selectedConnectionsCount }}</strong>
              </div>
              <div class="bulk-update-review-strip__item">
                <small>Ação pronta</small>
                <strong>{{ hasAnyAction ? 'Sim' : 'Não' }}</strong>
              </div>
            </div>

            <div class="bulk-update-stage__actions mt-4">
              <vs-button size="small" type="border" color="dark" @click="goToStep(2)">
                Voltar
              </vs-button>
              <vs-button size="small" color="primary" :disabled="!canAdvanceActionStep" @click="goToStep(4)">
                Avançar para revisão
              </vs-button>
            </div>
          </div>

          <div v-if="currentStep === 4" class="bulk-update-stage__surface">
            <div class="bulk-update-stage__header">
              <div>
                <span class="bulk-update-stage__eyebrow">Etapa 4</span>
                <h4>Revisão final</h4>
                <p>Confira o resumo completo do lote antes de executar a atualização em massa.</p>
              </div>
            </div>

            <div class="bulk-update-summary-grid mt-4">
              <div class="bulk-update-summary-card">
                <small>Tenants afetados</small>
                <strong>{{ reviewTenantGroups.length }}</strong>
              </div>

              <div class="bulk-update-summary-card">
                <small>Conexões afetadas</small>
                <strong>{{ selectedConnectionsCount }}</strong>
              </div>

              <div class="bulk-update-summary-card">
                <small>Configuração final</small>
                <strong>{{ configurationSummaryLabel }}</strong>
              </div>

              <div class="bulk-update-summary-card">
                <small>Status alterados</small>
                <strong>{{ statusSummaryLabel }}</strong>
              </div>
            </div>

            <div class="bulk-update-review-layout mt-4">
              <div class="bulk-update-review-card">
                <div class="bulk-update-review-card__header">
                  <strong>Resumo das alterações</strong>
                </div>

                <ul class="bulk-update-review-list">
                  <li>{{ configurationReviewText }}</li>
                  <li>{{ resetFinishedReviewText }}</li>
                  <li>{{ activatedReviewText }}</li>
                </ul>

                <div class="bulk-update-danger mt-4">
                  <strong>Atenção</strong>
                  <span>Esta ação não é reversível e pode impactar múltiplas dashes ao mesmo tempo.</span>
                </div>

                <div class="mt-4">
                  <vs-checkbox v-model="confirmationAccepted" size="small">
                    <span>Estou ciente de que a atualização em massa não pode ser desfeita automaticamente.</span>
                  </vs-checkbox>
                </div>
              </div>

              <div class="bulk-update-review-card">
                <div class="bulk-update-review-card__header">
                  <strong>Tenants e conexões afetadas</strong>
                </div>

                <div class="bulk-update-review-groups">
                  <div v-for="group in reviewTenantGroups" :key="`review-${group.tenantName}`" class="bulk-update-review-group">
                    <div class="bulk-update-review-group__header">
                      <strong>{{ tenantDisplayName(group.tenant) }}</strong>
                      <small class="ml-2">{{ group.selectedInstances.length }} conexão(ões)</small>
                    </div>

                    <ul class="bulk-update-review-group__list">
                      <li v-for="instance in group.selectedInstances" :key="`review-${group.tenantName}-${instance.id}`">
                        {{ instance.name }} <small>(ID {{ instance.id }} | {{ formatInstanceMode(instance) }})</small>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div class="bulk-update-stage__actions mt-4">
              <vs-button size="small" type="border" color="dark" @click="goToStep(3)">
                Voltar
              </vs-button>
              <vs-button
                size="small"
                color="danger"
                :disabled="!canSubmitBulkUpdate"
                @click="submitBulkUpdate"
              >
                {{ submitLoading ? 'Executando atualização...' : 'Confirmar atualização em massa' }}
              </vs-button>
            </div>

            <div v-if="bulkResult" class="bulk-update-result mt-4">
              <div class="bulk-update-result__header">
                <strong>Resultado da execução</strong>
                <span :class="bulkResult.summary.failed_connections > 0 ? 'text-warning' : 'text-success'">
                  {{ bulkResult.message }}
                </span>
              </div>

              <div class="bulk-update-summary-grid mt-4">
                <div class="bulk-update-summary-card">
                  <small>Conexões com sucesso</small>
                  <strong>{{ bulkResult.summary.successful_connections }}</strong>
                </div>

                <div class="bulk-update-summary-card">
                  <small>Conexões com falha</small>
                  <strong>{{ bulkResult.summary.failed_connections }}</strong>
                </div>

                <div class="bulk-update-summary-card">
                  <small>Tenants parciais</small>
                  <strong>{{ bulkResult.summary.partial_tenants }}</strong>
                </div>

                <div class="bulk-update-summary-card">
                  <small>Tenants concluídos</small>
                  <strong>{{ bulkResult.summary.successful_tenants }}</strong>
                </div>
              </div>

              <div v-if="failedResultItems.length" class="bulk-update-review-card mt-4">
                <div class="bulk-update-review-card__header">
                  <strong>Falhas encontradas</strong>
                </div>

                <ul class="bulk-update-review-list">
                  <li v-for="item in failedResultItems" :key="`failure-${item.tenantName}-${item.id}`">
                    {{ item.tenantName }} / {{ item.name || `Conexão ${item.id}` }}: {{ item.error }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import tenantsRequest from '@/http/requests/tenants';
import instanceHttp from '@/http/requests/connections';
import {
  DEFAULT_OFFICIAL_INSTANCE_PROVIDER,
  getOfficialProviderLabel,
  getOfficialProviderValueByIntegrationId,
  officialInstanceProviders
} from '@/supports/officialInstanceProviders';

const TENANTS_PER_PAGE = 6;

const createDefaultActionForm = () => ({
  configurationMode: 'keep',
  officialProvider: DEFAULT_OFFICIAL_INSTANCE_PROVIDER,
  webUnlimited: false,
  webUseSync: false,
  updateResetFinished: false,
  resetFinishedValue: true,
  updateActivated: false,
  activatedValue: true
});

export default {
  name: 'bulk-update-instances',
  data() {
    return {
      currentStep: 1,
      tenants: [],
      tenantSearch: '',
      currentTenantPage: 1,
      connectionSearch: '',
      selectedTenantNames: [],
      instancesByTenant: {},
      selectedConnectionsByTenant: {},
      tenantsWithoutConnections: [],
      tenantLoadErrors: [],
      loadingTenants: false,
      loadingInstances: false,
      actionForm: createDefaultActionForm(),
      confirmationAccepted: false,
      submitLoading: false,
      bulkResult: null,
      officialInstanceProviders
    };
  },
  computed: {
    ...mapGetters(['token']),
    sortedTenants() {
      return [...this.tenants]
        .filter(tenant => !tenant.deleted_at)
        .sort((left, right) => this.tenantName(left).localeCompare(this.tenantName(right)));
    },
    filteredTenants() {
      const search = this.tenantSearch.trim().toLowerCase();
      if (!search) {
        return this.sortedTenants;
      }

      return this.sortedTenants.filter(tenant => {
        const target = `${this.tenantName(tenant)} ${tenant.cnpj || ''}`.toLowerCase();
        return target.includes(search);
      });
    },
    totalTenantPages() {
      return Math.max(1, Math.ceil(this.filteredTenants.length / TENANTS_PER_PAGE));
    },
    tenantPageSize() {
      return TENANTS_PER_PAGE;
    },
    paginatedFilteredTenants() {
      const start = (this.currentTenantPage - 1) * TENANTS_PER_PAGE;
      return this.filteredTenants.slice(start, start + TENANTS_PER_PAGE);
    },
    tenantPaginationSummary() {
      if (!this.filteredTenants.length) {
        return 'Nenhum tenant encontrado.';
      }

      const start = ((this.currentTenantPage - 1) * TENANTS_PER_PAGE) + 1;
      const end = Math.min(this.currentTenantPage * TENANTS_PER_PAGE, this.filteredTenants.length);

      return `Exibindo ${start}-${end} de ${this.filteredTenants.length} tenant(s)`;
    },
    availableTenantGroups() {
      const search = this.connectionSearch.trim().toLowerCase();

      return this.selectedTenantNames
        .filter(tenantName => Array.isArray(this.instancesByTenant[tenantName]) && this.instancesByTenant[tenantName].length > 0)
        .map((tenantName) => {
          const instances = this.instancesByTenant[tenantName] || [];
          const filteredInstances = instances.filter((instance) => {
            if (!search) {
              return true;
            }

            const searchable = [
              instance.name,
              instance.phone_number,
              instance.id,
              this.formatInstanceMode(instance)
            ].join(' ').toLowerCase();

            return searchable.includes(search);
          });

          return {
            tenantName,
            tenant: this.findTenantByName(tenantName),
            instances,
            filteredInstances
          };
        });
    },
    selectedConnectionsCount() {
      return Object.values(this.selectedConnectionsByTenant).reduce((total, connectionIds) => {
        return total + connectionIds.length;
      }, 0);
    },
    selectedConnectionsFlat() {
      return Object.keys(this.selectedConnectionsByTenant).reduce((accumulator, tenantName) => {
        const ids = this.selectedConnectionsByTenant[tenantName] || [];
        const instances = this.instancesByTenant[tenantName] || [];

        ids.forEach((id) => {
          const instance = instances.find(item => item.id === id);
          if (instance) {
            accumulator.push(instance);
          }
        });

        return accumulator;
      }, []);
    },
    hasOfficialConnectionsSelected() {
      return this.selectedConnectionsFlat.some(instance => instance.is_official);
    },
    canAdvanceTenantStep() {
      return this.selectedTenantNames.length > 0 && !this.loadingInstances;
    },
    canAdvanceConnectionsStep() {
      return this.selectedConnectionsCount > 0;
    },
    canConfigureStatus() {
      return this.actionForm.configurationMode === 'web'
        || (this.actionForm.configurationMode === 'keep' && !this.hasOfficialConnectionsSelected);
    },
    statusLockMessage() {
      if (this.actionForm.configurationMode === 'official') {
        return 'Conexões oficiais seguem a mesma regra do popup individual e não permitem ajuste manual de status.';
      }

      return 'A seleção atual possui conexões oficiais. Remova essas conexões da seleção ou converta o lote para Web para liberar os ajustes de status.';
    },
    bulkChanges() {
      const changes = {};

      if (this.actionForm.configurationMode === 'official') {
        changes.isOfficial = true;
        changes.officialProvider = this.actionForm.officialProvider;
      }

      if (this.actionForm.configurationMode === 'web') {
        changes.isOfficial = false;
        changes.unlimitedInstance = this.actionForm.webUnlimited;
        changes.useSync = this.actionForm.webUseSync;
      }

      if (this.canConfigureStatus && this.actionForm.updateResetFinished) {
        changes.resetFinished = this.actionForm.resetFinishedValue;
      }

      if (this.canConfigureStatus && this.actionForm.updateActivated) {
        changes.activated = this.actionForm.activatedValue;
      }

      return changes;
    },
    hasAnyAction() {
      return Object.keys(this.bulkChanges).length > 0;
    },
    canAdvanceActionStep() {
      return this.canAdvanceConnectionsStep && this.hasAnyAction;
    },
    reviewTenantGroups() {
      return this.selectedTenantNames.reduce((accumulator, tenantName) => {
        const selectedIds = this.selectedConnectionsByTenant[tenantName] || [];
        if (!selectedIds.length) {
          return accumulator;
        }

        const instances = this.instancesByTenant[tenantName] || [];
        const selectedInstances = instances.filter(instance => selectedIds.includes(instance.id));

        accumulator.push({
          tenantName,
          tenant: this.findTenantByName(tenantName),
          selectedInstances
        });

        return accumulator;
      }, []);
    },
    configurationSummaryLabel() {
      if (this.actionForm.configurationMode === 'official') {
        return `Oficial / ${getOfficialProviderLabel(this.actionForm.officialProvider)}`;
      }

      if (this.actionForm.configurationMode === 'web') {
        return this.actionForm.webUseSync
          ? 'Web com Ativação Cloud'
          : (this.actionForm.webUnlimited ? 'Web Ilimitada' : 'Web');
      }

      return 'Sem alteração';
    },
    statusSummaryLabel() {
      const items = [];

      if (this.canConfigureStatus && this.actionForm.updateResetFinished) {
        items.push(`Reset ${this.actionForm.resetFinishedValue ? 'Sim' : 'Não'}`);
      }

      if (this.canConfigureStatus && this.actionForm.updateActivated) {
        items.push(`Ativa ${this.actionForm.activatedValue ? 'Sim' : 'Não'}`);
      }

      return items.length ? items.join(' | ') : 'Sem alteração';
    },
    configurationReviewText() {
      if (this.actionForm.configurationMode === 'official') {
        return `As conexões selecionadas serão convertidas para Oficial / Cloud usando o fornecedor ${getOfficialProviderLabel(this.actionForm.officialProvider)}.`;
      }

      if (this.actionForm.configurationMode === 'web') {
        return `As conexões selecionadas serão convertidas para Web${this.actionForm.webUnlimited ? ' com tipo ilimitado' : ''}${this.actionForm.webUseSync ? ' e Ativação Cloud' : ''}.`;
      }

      return 'A configuração atual das conexões será mantida.';
    },
    resetFinishedReviewText() {
      if (!this.canConfigureStatus || !this.actionForm.updateResetFinished) {
        return 'O status Reset Finalizado não será alterado.';
      }

      return `O status Reset Finalizado será marcado como ${this.actionForm.resetFinishedValue ? 'Sim' : 'Não'} para todas as conexões selecionadas.`;
    },
    activatedReviewText() {
      if (!this.canConfigureStatus || !this.actionForm.updateActivated) {
        return 'O status Conexão Ativa não será alterado.';
      }

      return `O status Conexão Ativa será marcado como ${this.actionForm.activatedValue ? 'Sim' : 'Não'} para todas as conexões selecionadas.`;
    },
    canSubmitBulkUpdate() {
      return this.canAdvanceActionStep && this.confirmationAccepted && !this.submitLoading;
    },
    bulkTargetsPayload() {
      return this.reviewTenantGroups.map(group => ({
        tenantName: group.tenantName,
        instances: group.selectedInstances.map(instance => ({
          id: instance.id,
          name: instance.name
        }))
      }));
    },
    failedResultItems() {
      if (!this.bulkResult || !this.bulkResult.results) {
        return [];
      }

      return this.bulkResult.results.reduce((accumulator, tenantResult) => {
        (tenantResult.instances || []).forEach((instanceResult) => {
          if (instanceResult.status === 'failed') {
            accumulator.push({
              tenantName: tenantResult.tenantName,
              id: instanceResult.id,
              name: instanceResult.name,
              error: instanceResult.error
            });
          }
        });

        return accumulator;
      }, []);
    }
  },
  watch: {
    'actionForm.configurationMode'() {
      this.applyActionRules();
    },
    tenantSearch() {
      this.currentTenantPage = 1;
    },
    totalTenantPages(value) {
      if (this.currentTenantPage > value) {
        this.currentTenantPage = value;
      }
    },
    canConfigureStatus(value) {
      if (!value) {
        this.actionForm.updateResetFinished = false;
        this.actionForm.updateActivated = false;
      }
    },
    selectedConnectionsCount() {
      if (this.currentStep > 2 && !this.canAdvanceConnectionsStep) {
        this.currentStep = 2;
      }
    }
  },
  methods: {
    verify() {
      const permissions = localStorage.getItem('permissions');
      const twoFactor = localStorage.getItem('twoFactor');

      if (!permissions && !twoFactor) {
        this.$router.push('/user-settings');
      } else if (!permissions && twoFactor) {
        this.$router.push('/two-factor');
      }
    },
    stepClass(step) {
      return {
        'bulk-update-step--active': this.currentStep === step,
        'bulk-update-step--complete': this.currentStep > step,
        'bulk-update-step--locked': !this.canVisitStep(step)
      };
    },
    canVisitStep(step) {
      if (step === 1) {
        return true;
      }

      if (step === 2) {
        return Object.keys(this.instancesByTenant).length > 0;
      }

      if (step === 3) {
        return this.canAdvanceConnectionsStep;
      }

      if (step === 4) {
        return this.canAdvanceActionStep;
      }

      return false;
    },
    goToStep(step) {
      if (!this.canVisitStep(step)) {
        return;
      }

      this.currentStep = step;
    },
    tenantName(tenant) {
      if (tenant && tenant.fqdn) {
        return tenant.fqdn.split('.')[0];
      }

      return tenant && tenant.name ? tenant.name : '';
    },
    tenantDisplayName(tenant) {
      const name = this.tenantName(tenant);
      return tenant && tenant.cnpj ? `${name} (${tenant.cnpj})` : name;
    },
    tenantKey(tenant) {
      return `${this.tenantName(tenant)}-${tenant && (tenant.website_id || tenant.id || 'no-id')}`;
    },
    findTenantByName(name) {
      return this.tenants.find(tenant => this.tenantName(tenant) === name) || null;
    },
    isTenantSelected(tenantName) {
      return this.selectedTenantNames.includes(tenantName);
    },
    toggleTenantSelection(tenantName, checked) {
      if (checked && !this.selectedTenantNames.includes(tenantName)) {
        this.selectedTenantNames.push(tenantName);
      }

      if (!checked) {
        this.selectedTenantNames = this.selectedTenantNames.filter(item => item !== tenantName);
      }

      this.resetSelectionAfterTenantChange();
    },
    selectAllFilteredTenants() {
      const selected = new Set(this.selectedTenantNames);
      this.filteredTenants.forEach((tenant) => {
        const tenantName = this.tenantName(tenant);
        if (tenantName) {
          selected.add(tenantName);
        }
      });

      this.selectedTenantNames = Array.from(selected);
      this.resetSelectionAfterTenantChange();
    },
    clearTenantSelection() {
      this.selectedTenantNames = [];
      this.resetSelectionAfterTenantChange();
    },
    previousTenantPage() {
      if (this.currentTenantPage === 1) {
        return;
      }

      this.currentTenantPage -= 1;
    },
    nextTenantPage() {
      if (this.currentTenantPage >= this.totalTenantPages) {
        return;
      }

      this.currentTenantPage += 1;
    },
    resetSelectionAfterTenantChange() {
      this.instancesByTenant = {};
      this.selectedConnectionsByTenant = {};
      this.tenantsWithoutConnections = [];
      this.tenantLoadErrors = [];
      this.connectionSearch = '';
      this.bulkResult = null;
      this.confirmationAccepted = false;

      if (this.currentStep > 1) {
        this.currentStep = 1;
      }
    },
    normalizeInstance(instance, tenantName) {
      return {
        ...instance,
        tenantName,
        official_provider: getOfficialProviderValueByIntegrationId(instance.integration_id)
      };
    },
    async fetchTenants() {
      this.loadingTenants = true;
      this.$store.dispatch('setShowLoadingOverlay', false);

      try {
        await this.$store.dispatch('getAllTenants', {
          token: this.token,
          filterUsers: true
        });

        this.tenants = this.$store.state.tenants.tenants || [];
      } catch (error) {
        console.error(error);
        this.tenants = [];
        this.$vs.notify({
          color: 'danger',
          title: 'Erro',
          text: 'Não foi possível carregar os tenants.'
        });
      } finally {
        this.$store.dispatch('setShowLoadingOverlay', true);
        this.loadingTenants = false;
      }
    },
    async loadConnectionsFromSelectedTenants() {
      if (!this.canAdvanceTenantStep) {
        return;
      }

      this.loadingInstances = true;
      this.bulkResult = null;
      this.confirmationAccepted = false;
      this.$store.dispatch('setShowLoadingOverlay', false);

      try {
        const responses = await Promise.allSettled(
          this.selectedTenantNames.map(tenantName => tenantsRequest.getInstances(tenantName, this.token))
        );

        const instancesByTenant = {};
        const selectedConnectionsByTenant = {};
        const tenantsWithoutConnections = [];
        const tenantLoadErrors = [];
        const availableTenantNames = [];

        responses.forEach((response, index) => {
          const tenantName = this.selectedTenantNames[index];

          if (response.status !== 'fulfilled') {
            tenantLoadErrors.push({
              tenantName,
              error: this.parseRequestError(response.reason)
            });
            return;
          }

          const normalizedInstances = (response.value.data || []).map(instance => this.normalizeInstance(instance, tenantName));
          if (!normalizedInstances.length) {
            tenantsWithoutConnections.push(tenantName);
            return;
          }

          instancesByTenant[tenantName] = normalizedInstances;
          selectedConnectionsByTenant[tenantName] = [];
          availableTenantNames.push(tenantName);
        });

        this.instancesByTenant = instancesByTenant;
        this.selectedConnectionsByTenant = selectedConnectionsByTenant;
        this.tenantsWithoutConnections = tenantsWithoutConnections;
        this.tenantLoadErrors = tenantLoadErrors;
        this.selectedTenantNames = availableTenantNames;

        if (!availableTenantNames.length) {
          this.$vs.notify({
            color: 'warning',
            title: 'Sem conexões',
            text: 'Nenhum tenant selecionado possui conexões disponíveis para atualização.'
          });
          return;
        }

        if (tenantsWithoutConnections.length || tenantLoadErrors.length) {
          this.$vs.notify({
            color: 'warning',
            title: 'Seleção ajustada',
            text: 'Alguns tenants foram removidos por não terem conexões ou por falha ao carregar os dados.'
          });
        }

        this.currentStep = 2;
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro',
          text: 'Não foi possível carregar as conexões dos tenants selecionados.'
        });
      } finally {
        this.$store.dispatch('setShowLoadingOverlay', true);
        this.loadingInstances = false;
      }
    },
    isConnectionSelected(tenantName, instanceId) {
      return (this.selectedConnectionsByTenant[tenantName] || []).includes(instanceId);
    },
    toggleConnectionSelection(tenantName, instanceId, checked) {
      const currentSelection = new Set(this.selectedConnectionsByTenant[tenantName] || []);

      if (checked) {
        currentSelection.add(instanceId);
      } else {
        currentSelection.delete(instanceId);
      }

      this.$set(this.selectedConnectionsByTenant, tenantName, Array.from(currentSelection));
      this.bulkResult = null;
      this.confirmationAccepted = false;
    },
    selectTenantVisibleConnections(tenantName) {
      const group = this.availableTenantGroups.find(item => item.tenantName === tenantName);
      if (!group) {
        return;
      }

      const selected = new Set(this.selectedConnectionsByTenant[tenantName] || []);
      group.filteredInstances.forEach(instance => selected.add(instance.id));
      this.$set(this.selectedConnectionsByTenant, tenantName, Array.from(selected));
    },
    clearTenantConnections(tenantName) {
      this.$set(this.selectedConnectionsByTenant, tenantName, []);
      this.bulkResult = null;
      this.confirmationAccepted = false;
    },
    selectAllVisibleConnections() {
      this.availableTenantGroups.forEach((group) => {
        const selected = new Set(this.selectedConnectionsByTenant[group.tenantName] || []);
        group.filteredInstances.forEach(instance => selected.add(instance.id));
        this.$set(this.selectedConnectionsByTenant, group.tenantName, Array.from(selected));
      });
    },
    clearAllConnectionsSelection() {
      Object.keys(this.selectedConnectionsByTenant).forEach((tenantName) => {
        this.$set(this.selectedConnectionsByTenant, tenantName, []);
      });

      this.bulkResult = null;
      this.confirmationAccepted = false;

      if (this.currentStep > 2) {
        this.currentStep = 2;
      }
    },
    applyActionRules() {
      if (this.actionForm.configurationMode !== 'official') {
        this.actionForm.officialProvider = DEFAULT_OFFICIAL_INSTANCE_PROVIDER;
      }

      if (this.actionForm.configurationMode !== 'web') {
        this.actionForm.webUnlimited = false;
        this.actionForm.webUseSync = false;
      }

      if (!this.canConfigureStatus) {
        this.actionForm.updateResetFinished = false;
        this.actionForm.updateActivated = false;
      }

      this.bulkResult = null;
      this.confirmationAccepted = false;
    },
    formatInstanceMode(instance) {
      if (instance.is_official) {
        return `Oficial / ${getOfficialProviderLabel(instance.official_provider || getOfficialProviderValueByIntegrationId(instance.integration_id))}`;
      }

      if (instance.use_sync) {
        return instance.type === 'web_ilimitada' ? 'Web Ilimitada + Ativação Cloud' : 'Web Ativação Cloud';
      }

      return instance.type === 'web_ilimitada' ? 'Web Ilimitada' : 'Web';
    },
    parseRequestError(error) {
      if (error && error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          return error.response.data;
        }

        if (error.response.data.message) {
          return error.response.data.message;
        }
      }

      return error && error.message ? error.message : 'Erro inesperado';
    },
    async submitBulkUpdate() {
      if (!this.canSubmitBulkUpdate) {
        return;
      }

      this.submitLoading = true;
      this.bulkResult = null;

      try {
        const response = await instanceHttp.bulkUpdateInstances({
          targets: this.bulkTargetsPayload,
          changes: this.bulkChanges
        }, this.token, 0);

        this.bulkResult = response.data;

        this.$vs.notify({
          color: response.data.summary.failed_connections > 0 ? 'warning' : 'success',
          title: response.data.summary.failed_connections > 0 ? 'Concluído com pendências' : 'Sucesso',
          text: response.data.message
        });
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro',
          text: this.parseRequestError(error)
        });
      } finally {
        this.submitLoading = false;
      }
    },
    resetWizard() {
      this.currentStep = 1;
      this.tenantSearch = '';
      this.currentTenantPage = 1;
      this.connectionSearch = '';
      this.selectedTenantNames = [];
      this.instancesByTenant = {};
      this.selectedConnectionsByTenant = {};
      this.tenantsWithoutConnections = [];
      this.tenantLoadErrors = [];
      this.actionForm = createDefaultActionForm();
      this.confirmationAccepted = false;
      this.submitLoading = false;
      this.bulkResult = null;
    }
  },
  async mounted() {
    this.verify();
    await this.fetchTenants();
  }
};
</script>

<style lang="scss">
#bulk-update-instances {
  padding-bottom: 24px;

  .bulk-update-shell {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .bulk-update-kicker,
  .bulk-update-stage__eyebrow,
  .bulk-update-sidebar__eyebrow,
  .bulk-update-card-head__eyebrow,
  .bulk-update-group__eyebrow {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    background: rgba(33, 117, 214, 0.1);
    color: #1d63b2;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    padding: 7px 12px;
    text-transform: uppercase;
  }

  .bulk-update-intro {
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    gap: 24px;
    border: 1px solid #e3edf8;
    border-radius: 32px;
    background:
      radial-gradient(circle at top right, rgba(37, 118, 214, 0.15), transparent 30%),
      linear-gradient(135deg, #ffffff 0%, #f7fbff 62%, #eef6ff 100%);
    box-shadow: 0 20px 55px rgba(16, 76, 148, 0.08);
    padding: 30px;
  }

  .bulk-update-intro::after {
    content: '';
    position: absolute;
    right: -80px;
    bottom: -120px;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: rgba(33, 117, 214, 0.08);
  }

  .bulk-update-intro__copy {
    position: relative;
    z-index: 1;
    max-width: 760px;
  }

  .bulk-update-intro__side {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 16px;
    min-width: 260px;
  }

  .bulk-update-instances__title {
    color: #203449;
    margin: 14px 0 10px;
  }

  .bulk-update-instances__subtitle {
    color: #66788f;
    line-height: 1.7;
    margin: 0;
  }

  .bulk-update-intro__stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
  }

  .bulk-update-intro__stat,
  .bulk-update-mini-stat {
    border: 1px solid #dbe8f7;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.84);
    backdrop-filter: blur(8px);
    padding: 16px;
  }

  .bulk-update-intro__stat small,
  .bulk-update-mini-stat small,
  .bulk-update-stage__counter,
  .bulk-update-review-strip__item small,
  .bulk-update-summary-card small {
    color: #6d7f96;
  }

  .bulk-update-intro__stat strong,
  .bulk-update-mini-stat strong,
  .bulk-update-review-strip__item strong,
  .bulk-update-summary-card strong {
    color: #203449;
    display: block;
    margin-top: 6px;
  }

  .bulk-update-wizard {
    display: grid;
    grid-template-columns: minmax(280px, 320px) minmax(0, 1fr);
    gap: 22px;
    align-items: flex-start;
  }

  .bulk-update-sidebar {
    display: flex;
    flex-direction: column;
    gap: 18px;
    position: sticky;
    top: 88px;
  }

  .bulk-update-sidebar__panel,
  .bulk-update-stage__surface,
  .bulk-update-group,
  .bulk-update-config-card,
  .bulk-update-review-card,
  .bulk-update-summary-card,
  .bulk-update-result {
    border: 1px solid #e3edf8;
    border-radius: 28px;
    background: #ffffff;
    box-shadow: 0 18px 45px rgba(16, 76, 148, 0.06);
  }

  .bulk-update-sidebar__panel {
    padding: 22px;
  }

  .bulk-update-sidebar__header {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 18px;
  }

  .bulk-update-sidebar__header strong,
  .bulk-update-card-head h5,
  .bulk-update-group__copy h5 {
    color: #203449;
  }

  .bulk-update-sidebar__steps {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .bulk-update-sidebar-step {
    position: relative;
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 14px;
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 6px 0 14px;
    transition: 0.2s ease;
  }

  .bulk-update-sidebar-step:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 44px;
    left: 20px;
    width: 2px;
    height: calc(100% - 24px);
    background: #d7e5f8;
  }

  .bulk-update-sidebar-step__marker {
    position: relative;
    z-index: 1;
    width: 40px;
    height: 40px;
    border-radius: 16px;
    background: #eef4fb;
    color: #557190;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    box-shadow: 0 0 0 8px #fff;
  }

  .bulk-update-sidebar-step__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .bulk-update-sidebar-step__content small {
    color: #8193a9;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .bulk-update-sidebar-step__content strong {
    color: #24384d;
  }

  .bulk-update-sidebar-step__content span {
    color: #6f8398;
    line-height: 1.5;
    font-size: 0.92rem;
  }

  .bulk-update-step--active .bulk-update-sidebar-step__marker,
  .bulk-update-step--complete .bulk-update-sidebar-step__marker {
    background: linear-gradient(135deg, #1d63b2 0%, #3b86ea 100%);
    color: #fff;
  }

  .bulk-update-step--active .bulk-update-sidebar-step__content strong {
    color: #1d63b2;
  }

  .bulk-update-step--complete::after {
    background: linear-gradient(180deg, #1d63b2 0%, #79aef2 100%);
  }

  .bulk-update-step--locked {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .bulk-update-sidebar__mini-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .bulk-update-sidebar__hint {
    margin-top: 16px;
    border-radius: 20px;
    background: linear-gradient(180deg, #f7fbff 0%, #eef5ff 100%);
    padding: 16px;
  }

  .bulk-update-sidebar__hint strong {
    display: block;
    color: #24446f;
    margin-bottom: 6px;
  }

  .bulk-update-sidebar__hint p {
    margin: 0;
    color: #667a90;
    line-height: 1.6;
  }

  .bulk-update-stage__surface {
    position: relative;
    overflow: hidden;
    padding: 28px;
  }

  .bulk-update-stage__surface::before {
    content: '';
    position: absolute;
    top: -120px;
    right: -80px;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: rgba(59, 134, 234, 0.08);
  }

  .bulk-update-stage__header,
  .bulk-update-group__header,
  .bulk-update-review-card__header,
  .bulk-update-result__header,
  .bulk-update-stage__actions,
  .bulk-update-toolbar,
  .bulk-update-toggle-card,
  .bulk-update-connection-card__top {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
  }

  .bulk-update-stage__header {
    position: relative;
    z-index: 1;
  }

  .bulk-update-stage__header h4 {
    color: #203449;
    margin: 14px 0 8px;
  }

  .bulk-update-stage__header p,
  .bulk-update-search-card__copy p,
  .bulk-update-card-head small,
  .bulk-update-group__copy small,
  .bulk-update-tenant-card__body small,
  .bulk-update-connection-card__identity small,
  .bulk-update-review-group__header small {
    color: #6f8398;
    line-height: 1.6;
    margin: 0;
  }

  .bulk-update-stage__counter {
    white-space: nowrap;
    border-radius: 999px;
    background: #eef5ff;
    color: #1d63b2;
    font-size: 0.85rem;
    font-weight: 700;
    padding: 12px 16px;
  }

  .bulk-update-search-card {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    gap: 18px;
    border: 1px solid #e8f1fb;
    border-radius: 24px;
    background: linear-gradient(180deg, #fbfdff 0%, #f3f8ff 100%);
    padding: 20px;
  }

  .bulk-update-search-card__copy strong {
    display: block;
    color: #24446f;
    margin-bottom: 8px;
  }

  .bulk-update-toolbar {
    align-items: flex-end;
  }

  .bulk-update-toolbar__actions,
  .bulk-update-group__actions,
  .bulk-update-inline-options {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .bulk-update-checkbox {
    accent-color: #2175d6;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .bulk-update-tenant-grid,
  .bulk-update-connection-grid {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .bulk-update-pagination {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: center;
    border: 1px solid #e8f1fb;
    border-radius: 22px;
    background: linear-gradient(180deg, #fbfdff 0%, #f5f9ff 100%);
    padding: 16px 18px;
  }

  .bulk-update-pagination__info strong {
    display: block;
    color: #24446f;
    margin-bottom: 6px;
  }

  .bulk-update-pagination__info small,
  .bulk-update-pagination__current {
    color: #6f8398;
    line-height: 1.5;
  }

  .bulk-update-pagination__controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .bulk-update-pagination__current {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 36px;
    padding: 0 12px;
    border-radius: 999px;
    background: #eef5ff;
    color: #1d63b2;
    font-size: 0.84rem;
    font-weight: 700;
    white-space: nowrap;
  }

  .bulk-update-groups,
  .bulk-update-review-groups {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .bulk-update-tenant-card,
  .bulk-update-choice-card,
  .bulk-update-provider-card,
  .bulk-update-connection-card {
    border: 1px solid #e6eef8;
    border-radius: 24px;
    background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
    transition: 0.2s ease;
  }

  .bulk-update-tenant-card {
    padding: 18px;
    cursor: pointer;
  }

  .bulk-update-tenant-card:hover,
  .bulk-update-choice-card:hover,
  .bulk-update-provider-card:hover,
  .bulk-update-connection-card:hover {
    transform: translateY(-2px);
    border-color: #cfe1f8;
    box-shadow: 0 14px 30px rgba(33, 117, 214, 0.08);
  }

  .bulk-update-tenant-card--selected,
  .bulk-update-choice-card--selected,
  .bulk-update-provider-card--selected,
  .bulk-update-connection-card--selected {
    border-color: #3b86ea;
    background: linear-gradient(180deg, #fafdff 0%, #eef5ff 100%);
    box-shadow: 0 16px 34px rgba(33, 117, 214, 0.12);
  }

  .bulk-update-tenant-card__top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    margin-bottom: 18px;
  }

  .bulk-update-tenant-card__avatar {
    width: 46px;
    height: 46px;
    border-radius: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1d63b2 0%, #3b86ea 100%);
    color: #fff;
    font-weight: 700;
    font-size: 1rem;
  }

  .bulk-update-tenant-card__body strong,
  .bulk-update-connection-card__identity strong,
  .bulk-update-choice-card__content strong,
  .bulk-update-provider-card__content strong {
    color: #203449;
    display: block;
  }

  .bulk-update-tenant-card__body small,
  .bulk-update-choice-card__content small,
  .bulk-update-provider-card__content small {
    display: block;
    margin-top: 6px;
  }

  .bulk-update-tenant-card__footer {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid #ebf2fb;
    color: #1d63b2;
    font-size: 0.86rem;
    font-weight: 600;
  }

  .bulk-update-empty {
    border: 1px dashed #cad8eb;
    border-radius: 24px;
    padding: 28px;
    text-align: center;
    color: #72859b;
    background: #fbfdff;
    grid-column: 1 / -1;
  }

  .bulk-update-group {
    padding: 22px;
  }

  .bulk-update-group__copy h5 {
    margin: 12px 0 6px;
  }

  .bulk-update-connection-grid {
    margin-top: 18px;
  }

  .bulk-update-connection-card {
    padding: 18px;
    cursor: pointer;
  }

  .bulk-update-connection-card__badges {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 16px;
  }

  .bulk-badge {
    border-radius: 999px;
    padding: 7px 11px;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
    display: inline-flex;
    align-items: center;
  }

  .bulk-badge--primary {
    background: #e8f1ff;
    color: #255faf;
  }

  .bulk-badge--success {
    background: #e8f8ee;
    color: #26734a;
  }

  .bulk-badge--neutral {
    background: #edf2f8;
    color: #63758c;
  }

  .bulk-update-config-grid,
  .bulk-update-review-layout {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: 16px;
  }

  .bulk-update-config-card,
  .bulk-update-review-card,
  .bulk-update-result {
    padding: 22px;
  }

  .bulk-update-card-head h5 {
    margin: 12px 0 8px;
  }

  .bulk-update-mode-grid,
  .bulk-update-provider-grid {
    display: grid;
    gap: 12px;
  }

  .bulk-update-choice-card,
  .bulk-update-provider-card {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 16px 18px;
    cursor: pointer;
  }

  .bulk-update-choice-card__content,
  .bulk-update-provider-card__content {
    display: flex;
    flex-direction: column;
  }

  .bulk-update-config-block {
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid #ebf2fb;
  }

  .bulk-update-config-label {
    display: block;
    color: #50657d;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    margin-bottom: 12px;
    text-transform: uppercase;
  }

  .bulk-update-toggle-card,
  .bulk-update-status-card {
    border: 1px solid #e6eef8;
    border-radius: 22px;
    background: linear-gradient(180deg, #fbfdff 0%, #f4f8ff 100%);
    padding: 16px 18px;
  }

  .bulk-update-toggle-card strong {
    display: block;
    color: #203449;
    margin-bottom: 6px;
  }

  .bulk-update-toggle-card small {
    color: #6f8398;
    line-height: 1.5;
  }

  .bulk-update-status-card__header {
    margin-bottom: 12px;
  }

  .bulk-update-status-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .bulk-update-status-list--disabled {
    opacity: 0.58;
  }

  .bulk-update-alert-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .bulk-update-alert,
  .bulk-update-danger {
    border-radius: 22px;
    padding: 16px 18px;
    line-height: 1.6;
  }

  .bulk-update-alert {
    border: 1px solid #d9e6f7;
    background: #f6faff;
    color: #4c617a;
  }

  .bulk-update-alert strong,
  .bulk-update-danger strong {
    display: block;
    margin-bottom: 6px;
  }

  .bulk-update-alert--warning,
  .bulk-update-danger {
    border-color: #f1c983;
    background: #fff6e7;
    color: #875715;
  }

  .bulk-update-alert--danger {
    border-color: #e7b1b1;
    background: #fff1f1;
    color: #a23f3f;
  }

  .bulk-update-review-strip,
  .bulk-update-summary-grid {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
  }

  .bulk-update-review-strip__item,
  .bulk-update-summary-card {
    padding: 18px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  }

  .bulk-update-review-list,
  .bulk-update-review-group__list {
    margin: 16px 0 0;
    padding-left: 18px;
    color: #425363;
    line-height: 1.8;
  }

  .bulk-update-review-group {
    border: 1px solid #e6eef8;
    border-radius: 22px;
    background: #fbfdff;
    padding: 16px 18px;
  }

  .bulk-update-result {
    border-color: #d8e8d8;
    background: linear-gradient(180deg, #fbfffb 0%, #f4fff4 100%);
  }

  .bulk-update-stage__actions {
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  @media (max-width: 1260px) {
    .bulk-update-wizard {
      grid-template-columns: minmax(260px, 300px) minmax(0, 1fr);
    }

    .bulk-update-tenant-grid,
    .bulk-update-connection-grid,
    .bulk-update-review-strip,
    .bulk-update-summary-grid,
    .bulk-update-alert-grid,
    .bulk-update-config-grid,
    .bulk-update-review-layout {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 992px) {
    .bulk-update-intro,
    .bulk-update-stage__header,
    .bulk-update-toolbar,
    .bulk-update-pagination,
    .bulk-update-group__header,
    .bulk-update-review-card__header,
    .bulk-update-result__header,
    .bulk-update-stage__actions,
    .bulk-update-toggle-card,
    .bulk-update-connection-card__top,
    .bulk-update-intro__side {
      flex-direction: column;
      align-items: stretch;
    }

    .bulk-update-wizard,
    .bulk-update-search-card {
      grid-template-columns: minmax(0, 1fr);
    }

    .bulk-update-sidebar {
      position: static;
    }

    .bulk-update-intro__side {
      min-width: 0;
    }
  }

  @media (max-width: 768px) {
    .bulk-update-intro,
    .bulk-update-stage__surface,
    .bulk-update-sidebar__panel,
    .bulk-update-group,
    .bulk-update-config-card,
    .bulk-update-review-card,
    .bulk-update-result {
      border-radius: 22px;
      padding: 20px;
    }

    .bulk-update-intro__stats,
    .bulk-update-sidebar__mini-grid,
    .bulk-update-tenant-grid,
    .bulk-update-connection-grid,
    .bulk-update-review-strip,
    .bulk-update-summary-grid,
    .bulk-update-alert-grid,
    .bulk-update-config-grid,
    .bulk-update-review-layout {
      grid-template-columns: minmax(0, 1fr);
    }

    .bulk-update-sidebar-step {
      grid-template-columns: 34px minmax(0, 1fr);
    }

    .bulk-update-sidebar-step__marker {
      width: 34px;
      height: 34px;
      border-radius: 14px;
    }

    .bulk-update-sidebar-step:not(:last-child)::after {
      left: 16px;
      top: 38px;
    }
  }
}
</style>